import { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
} from "lexical";

import {
  draftRawToLexicalJson,
  isDraftRawState,
} from "../legacy/draftToLexical";
import { $isMentionNode } from "../nodes/MentionNode";

// Lexical batches editor.update by default and commits on a microtask.
// setEditorState commits immediately, so batching would make importState
// synchronous while resetState was not. The consumers were written against
// Draft.js, where every one of these calls took effect at once, so the
// update-based methods are marked discrete to keep the whole surface
// synchronous.
const DISCRETE = { discrete: true };

/**
 * Exposes the imperative editor API that RichTextEditor's five consumers
 * call, and keeps the `editorState` prop in sync.
 *
 * The handle lives here rather than on the host component because every
 * method needs the editor instance, which only exists inside the composer.
 */
function EditorApiPlugin({ apiRef, editorState, content }) {
  const [editor] = useLexicalComposerContext();
  // NoteItem rebuilds its editorState object on every render, so the guard
  // has to compare content rather than identity.
  const lastImported = useRef(null);

  const importStateFromHtml = useCallback(
    html => {
      lastImported.current = html;
      editor.update(() => {
        const dom = new DOMParser().parseFromString(html, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.select();
        $insertNodes(nodes);
      }, DISCRETE);
    },
    [editor]
  );

  const importState = useCallback(
    state => {
      lastImported.current = JSON.stringify(state);
      const json = isDraftRawState(state)
        ? draftRawToLexicalJson(state)
        : state;
      // Outside editor.update: setEditorState replaces the state wholesale.
      editor.setEditorState(editor.parseEditorState(json));
    },
    [editor]
  );

  useImperativeHandle(
    apiRef,
    () => ({
      importState,

      importStateFromHtml,

      // Inserts at the caret, the way a paste does, so a template can be
      // dropped into the middle of a note the user has already started.
      appendHtml: html => {
        editor.update(() => {
          const dom = new DOMParser().parseFromString(html, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          // No selection means the editor has not been focused yet; take the
          // caret to be the end of the document so the nodes have somewhere
          // to go rather than being dropped.
          if ($getSelection() === null) {
            $getRoot().selectEnd();
          }
          $insertNodes(nodes);
        }, DISCRETE);
      },

      importStateFromPlain: text => {
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          for (const line of String(text ?? "").split("\n")) {
            const paragraph = $createParagraphNode();
            if (line) {
              paragraph.append($createTextNode(line));
            }
            root.append(paragraph);
          }
        }, DISCRETE);
      },

      exportState: () => editor.getEditorState().toJSON(),

      // The note's info.mentioned_users list, which the mentioned-user filter
      // matches on. Read from the document rather than tracked as the user
      // types, so removing a mention removes it from the list too.
      getMentionedUsers: () =>
        editor.read(() => {
          const seen = new Map();
          for (const node of $getRoot().getAllTextNodes()) {
            if ($isMentionNode(node) && node.__userId != null) {
              seen.set(node.__userId, {
                id: node.__userId,
                name: node.__mention,
              });
            }
          }
          return [...seen.values()];
        }),

      exportStateAsHtml: () =>
        editor.read(() => $generateHtmlFromNodes(editor, null)),

      exportStateAsPlain: () => editor.read(() => $getRoot().getTextContent()),

      resetState: () => {
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          // A root with no children is not a valid editor state.
          root.append($createParagraphNode());
        }, DISCRETE);
      },
    }),
    [editor, importState, importStateFromHtml]
  );

  useEffect(() => {
    if (!editorState) {
      return;
    }
    if (JSON.stringify(editorState) === lastImported.current) {
      return;
    }
    importState(editorState);
  }, [editorState, importState]);

  useEffect(() => {
    if (!content || content === lastImported.current) {
      return;
    }
    importStateFromHtml(content);
  }, [content, importStateFromHtml]);

  return null;
}

EditorApiPlugin.propTypes = {
  apiRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  editorState: PropTypes.object,
  content: PropTypes.string,
};

export default EditorApiPlugin;
